
import FileExaminationComments from './FileExaminationComments.vue';
import FileGenerationHistory from './FileGenerationHistory.vue';
import FileMetadata from './FileMetadata.vue';
import FileModificationOverview from './FileModificationOverview.vue';
import FileOptions from './FileOptions.vue';
import FileOriginatingDevice from './FileOriginatingDevice.vue';
import FileProprietaryData from './FileProprietaryData.vue';
import amplitudeService from '@/services/AmplitudeService';
import fileService from '@/services/ProjectFileService';
import { EvaStatus, MediaType } from '@/types/resource.types';
import FileStructuralReportCard from './FileStructuralReportCard.vue';
import FileStructureDataModal from './FileStructureDataModal.vue';
import FileC2PA from './FileC2PAResult.vue';
import FileECA from './FileECA.vue';
import FileSummary from './FileSummary.vue';
import FileUnknownData from './FileUnknownData.vue';
import MedexInsights from './InsightsCard.vue';
import PublicFileLogModal from './PublicFileLogModal.vue';
import UsesResultsStore from '@/components/hoc/UsesResultsStore';
import { Component, Watch } from 'vue-property-decorator';
import { IFileStructure, IFileStructureData } from '@/types/resource.types';
import { Route } from 'vue-router';
import { Get } from 'vuex-pathify';
import { paths } from '@/router/route-paths';

@Component({
    name: 'ProjectFileExaminationView',
    components: {
        FileExaminationComments,
        FileGenerationHistory,
        FileMetadata,
        FileModificationOverview,
        FileOptions,
        FileOriginatingDevice,
        FileStructuralReportCard,
        FileStructureDataModal,
        FileSummary,
        FileUnknownData,
        FileProprietaryData,
        PublicFileLogModal,
        MedexInsights,
        FileC2PA,
        FileECA
    }
})
export default class ProjectFileExaminationView extends UsesResultsStore {

    /* Data
    ============================================*/

    @Get('results/projectFileIds')
    readonly projectFileIds: number[];

    @Get('results/projectSummaryPath')
    readonly projectSummaryPath: string | null;

    showFileLogModal: boolean = false;
    showStructureDataModal: boolean = false;
    showStructuralReport: boolean = false;
    activeTab: number = 0;
    private _evaPoller: ReturnType<typeof setInterval> | null = null;
    fileStructure: IFileStructure | null = null;
    fileStructureData: IFileStructureData | null = null;

    /* Computed
    ============================================*/

    get isDocument(): boolean {
        return this.fileResult?.mediaType === MediaType.Document;
    }

    get currentFileIndex(): number {
        if (!this.file || !this.projectFileIds?.length) return -1;
        return this.projectFileIds.indexOf(this.file.id);
    }

    get hasSiblingFiles(): boolean {
        return this.projectFileIds?.length > 1 && this.currentFileIndex !== -1;
    }

    get hasPrevFile(): boolean {
        return this.currentFileIndex > 0;
    }

    get hasNextFile(): boolean {
        return this.currentFileIndex !== -1 && this.currentFileIndex < this.projectFileIds.length - 1;
    }

    /* Methods
    ============================================*/

    goToPrevFile() {
        if (!this.hasPrevFile) return;
        this.$router.push(paths.FILE_ANALYSIS(this.projectFileIds[this.currentFileIndex - 1]));
    }

    goToNextFile() {
        if (!this.hasNextFile) return;
        this.$router.push(paths.FILE_ANALYSIS(this.projectFileIds[this.currentFileIndex + 1]));
    }

    get classifiedBrand(): string | null {
        if(this.fileResult?.originatingDevice?.classifiedBrand ) {
            return this.fileResult.originatingDevice.classifiedBrand;
        }else if(this.fileResult?.brandClassification?.predictedValue){
            return this.fileResult.brandClassification.predictedValue;
        }
        return null;
    }

    get classifiedModel(): string | null {
        if(this.fileResult?.originatingDevice?.classifiedDevice) {
            return this.fileResult.originatingDevice.classifiedDevice;
        }else if(this.fileResult?.modelClassification?.predictedValue){
            return this.fileResult.modelClassification.predictedValue;
        }
        return null;
    }

    goToProject() {
        if(!this.file) return;
        this.$router.push(this.projectSummaryPath ?? this.$paths.PROJECT_SUMMARY(this.file.projectId));
    }

    async viewStructureData(fileStructure: IFileStructure, isUnknown: boolean = false) {
        if(!this.file?.id || !this.$hasAdvancedSubscription) return;
        try {
            this.$loading = true;
            let data = isUnknown
                ? await fileService.getUnknownStructure(this.file.id, fileStructure.id)
                : await fileService.getProprietaryStructure(this.file.id, fileStructure.id);
            this.fileStructure = fileStructure;
            this.fileStructureData = data;
            this.showStructureDataModal = true;
        } catch(e) {
            this.$logger.logError(e);
            this.$toast.error('There was an error loading the data structure. Please contact support');
        } finally {
            this.$loading = false;
        }
    }

    /* Lifecyce Hooks
    ============================================*/

    startEvaPoller() {
        this.stopEvaPoller();
        this._evaPoller = setInterval(async () => {
            if (!this.file) return;
            const status = this.file.evaStatus;
            if (status !== EvaStatus.Pending && status !== EvaStatus.Running) {
                this.stopEvaPoller();
                return;
            }
            await this.loadFileResult(this.file.id);
        }, 15000);
    }

    stopEvaPoller() {
        if (this._evaPoller !== null) {
            clearInterval(this._evaPoller);
            this._evaPoller = null;
        }
    }

    async beforeMount() {
        await this.loadFileResult(this.$route.params?.id);
        if (this.file) {
            amplitudeService.trackFileOpened({
                fileId: this.$route.params?.id,
                projectId: this.file.projectId,
                userTenancyAccount: (this as any).$store.state.subscriber?.id,
                openedByUser: (this as any).$user?.email
            });
        }
        if (this.file && !this.project) {
            this.loadProjectSummary(this.file.projectId);
        }
        if (this.file?.evaStatus === EvaStatus.Pending || this.file?.evaStatus === EvaStatus.Running) {
            this.startEvaPoller();
        }
    }

    beforeDestroy() {
        this.stopEvaPoller();
    }

    /* Watchers
    ============================================*/

    @Watch('$route')
    async onRouteChanged($routeNew: Route) {
        let id = parseInt($routeNew?.params?.id);
        if(id === this.file?.id) return;
        this.stopEvaPoller();
        await this.loadFileResult(id);
        if (this.file && !this.project) {
            this.loadProjectSummary(this.file.projectId);
        }
        if (this.file?.evaStatus === EvaStatus.Pending || this.file?.evaStatus === EvaStatus.Running) {
            this.startEvaPoller();
        }
    }
    @Watch('showStructuralReport')
    onStructuralReportChanged() {
    }
}
